<script setup lang="ts">
import {reactive, ref} from "vue";
import {OrderService} from "@/api/api.ts";
import {CourseApplyInfo} from "@/types";
import {ElMessageBox, ElNotification} from "element-plus";
import {TimeFormatter} from "@/utils/common.ts";
import AddSingleStudentDialog from "components/dialog/apply/AddSingleStudentDialog.vue";
import AddMultiStudentDialog from "components/dialog/apply/AddMultiStudentDialog.vue";

const addSingleStudentDialog = ref<InstanceType<typeof AddSingleStudentDialog>>();
const addMultiStudentDialog = ref<InstanceType<typeof AddMultiStudentDialog>>();

let courseId = 0
const pagination = reactive({
  current: 1,
  perPage: 10,
  total: 0
})

const studentList = ref<CourseApplyInfo[]>([])
const loading = ref(false)
const visible = ref(false)
const buySource = ref(0)
const isPay = ref(0)
const uContent = ref("")
const mContent = ref("")
const order = ref("")
const isApi = ref(-1)
const sourceList = [
  {id: 0, name: "报名来源"},
  {id: 1, name: "用户购买"},
  {id: 2, name: "老师添加"},
  {id: 3, name: "系统添加"},
];

const typeList = [
  {id: 0, name: "课程类型"},
  {id: 1, name: "付费课"},
  {id: 2, name: "免费课"},
];

const onSearch = () => {
  pagination.current = 1
  getStudentList()
}

const onReset = () => {
  buySource.value = 0
  isPay.value = 0
  uContent.value = ""
  mContent.value = ""
  order.value = ""
  isApi.value = -1
  onSearch()
}

const getStudentList = async () => {
  try {
    loading.value = true
    const res = await OrderService.getCourseApplyList({
      page: pagination.current,
      pageSize: pagination.perPage,
      course_id: courseId,
      buy_source: buySource.value,
      u_content: uContent.value,
      m_content: mContent.value,
      is_pay: isPay.value,
      order: order.value,
      is_api: isApi.value === -1 ? undefined : isApi.value,
    })
    if (res.data.code === 200) {
      studentList.value = res.data.data?.list || []
      pagination.total = res.data.data?.count || 0
      pagination.current = res.data.data?.page || 1
    } else {
      ElNotification.error({
        message: res.data.msg
      })
    }
  } catch (error) {
    console.log(error)
  } finally {
    loading.value = false
  }
}

const show = (id: number) => {
  courseId = id
  visible.value = true
  onSearch()
}

const hide = () => {
  visible.value = false
}

const deleteStudent = async (record: CourseApplyInfo) => {
  try {
    await ElMessageBox.confirm(
        '确认删除该学员吗',
        '注意',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
    )
    const res = await OrderService.delCourseOrder({
      course_id: courseId,
      order_id: record.id,
      user_id: record.user_id,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg
      })
      onSearch()
    } else {
      ElNotification.error({
        message: res.data.msg
      })
    }
  } catch (e) {
    console.log(e)
  }
}

const onPageChange = (page: number) => {
  pagination.current = page
  getStudentList()
}

const onPageSizeChange = (size: number) => {
  pagination.perPage = size
  getStudentList()
}

const onAddStudent = () => {
  addSingleStudentDialog.value?.show(courseId)
}

const onBatchAddStudent = () => {
  addMultiStudentDialog.value?.show(courseId)
}

defineExpose({
  show, hide
})
</script>

<template>
  <el-dialog v-model="visible" title="学生列表" align-center width="80%">
    <div class="flex items-center">
      <el-button type="primary" plain @click="onAddStudent">添加学员</el-button>
      <el-button type="primary" @click="onBatchAddStudent">批量添加学员</el-button>
    </div>
    <div class="font-bold text-1rem mt-1rem">
      如果此前已报名课程，继续添加则更新学员的支付时间
    </div>
    <div class="flex items-center gap-1.25rem mt-1.5rem flex-wrap">
      <el-input v-model="uContent" placeholder="搜索用户名" style="width: 12.5rem" @change="onSearch"></el-input>
      <el-input v-model="mContent" placeholder="搜索手机号" style="width: 12.5rem" @change="onSearch"></el-input>
      <el-input v-model="order" placeholder="搜索订单号" style="width: 12.5rem" @change="onSearch"></el-input>
      <el-select v-model="isApi" placeholder="是否来自接口" style="width: 12.5rem">
        <el-option label="是否来自接口" :value="-1"></el-option>
        <el-option label="是" :value="1"></el-option>
        <el-option label="否" :value="0"></el-option>
      </el-select>
      <el-select v-model="buySource" style="width: 12.5rem">
        <el-option v-for="item in sourceList" :value="item.id" :label="item.name" :key="item.id">
        </el-option>
      </el-select>
      <el-select v-model="isPay" style="width: 12.5rem">
        <el-option v-for="item in typeList" :value="item.id" :label="item.name" :key="item.id">
        </el-option>
      </el-select>
      <div class="flex items-center">
        <el-button type="primary" @click="onSearch">搜索</el-button>
        <el-button type="primary" plain @click="onReset">重置</el-button>
      </div>
    </div>
    <div class="mt-1rem">
      <el-table :data="studentList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="序号"/>
        <el-table-column prop="user_name" label="用户名"/>
        <el-table-column prop="mobile" label="手机号"/>
        <el-table-column prop="create_time" label="下单时间">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.create_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column prop="pay_time" label="支付时间">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.pay_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column prop="view_start_time" label="开始观看时间">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.view_start_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column prop="buy_source" label="报名来源">
          <template #default="scope">
            {{
              scope.row.buy_source === 1 ? "用户购买"
                  : scope.row.buy_source === 2 ? "老师添加" : scope.row.buy_source === 3 ? "系统添加" : '--'
            }}
          </template>
        </el-table-column>
        <el-table-column prop="lately_study_time" label="最近学习时间">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.lately_study_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="操作">
          <template #default="scope">
            <el-button type="danger" @click="deleteStudent(scope.row)">删除学员</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          layout="prev, pager, next, total, ->, jumper"
          :page-size="pagination.perPage"
          :total="pagination.total"
          :current-page="pagination.current"
          @current-change="onPageChange"
          @size-change="onPageSizeChange"
          class="mt-1rem"/>
    </div>
  </el-dialog>
  <add-multi-student-dialog ref="addMultiStudentDialog" @on-add-success="onSearch"/>
  <add-single-student-dialog ref="addSingleStudentDialog" @on-add-success="onSearch"/>
</template>

<style scoped>
</style>
